import FeatureFlagAPI from '../Api/feature-flag';

const LOCAL_STORAGE_FLAGS_KEY = 'vp-feature-flags';
const LOCAL_STORAGE_FLAG_TTL = 1000 * 60 * 10; // 10 min

interface FeatureFlags {
  [key: string]: boolean;
}

interface CachedFlags {
  flags: FeatureFlags;
  timestamp: number;
}

function getCachedFlags(): FeatureFlags | null {
  const raw = localStorage.getItem(LOCAL_STORAGE_FLAGS_KEY);
  if (!raw) return null;

  try {
    const { flags, timestamp }: CachedFlags = JSON.parse(raw);
    if (!timestamp || Date.now() - timestamp > LOCAL_STORAGE_FLAG_TTL) {
      return null;
    }
    console.log(
      `Flags get from cache. Will be invalidated in ${
        (LOCAL_STORAGE_FLAG_TTL - (Date.now() - timestamp)) / 1000
      } seconds`,
    );
    return flags;
  } catch (error) {
    console.error('Error parsing cached feature flags:', error);
    return null;
  }
}

const overrideFlagsFromEnv = (flags: FeatureFlags): FeatureFlags => {
  if (!process.env.REACT_APP_FEATURE_FLAGS_OVERRIDE) {
    return flags;
  }

  try {
    const overrideFlags: FeatureFlags = JSON.parse(
      process.env.REACT_APP_FEATURE_FLAGS_OVERRIDE,
    );
    const newFlags = { ...flags };
    Object.keys(overrideFlags).forEach((key) => {
      if (flags[key] !== undefined) {
        newFlags[key] = overrideFlags[key];
      }
    });
    return newFlags;
  } catch (error) {
    console.error('Error parsing feature flags override:', error);
    return flags;
  }
};

export const featureFlags = async (): Promise<FeatureFlags> => {
  if (process.env.NODE_ENV === 'test') return {};

  if (
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_FEATURE_FLAGS_OVERRIDE
  ) {
    try {
      return JSON.parse(process.env.REACT_APP_FEATURE_FLAGS_OVERRIDE);
    } catch (error) {
      console.error('Error parsing development feature flags override:', error);
      return {};
    }
  }

  const cached = getCachedFlags();
  if (cached) return overrideFlagsFromEnv(cached);

  try {
    const flags: FeatureFlags = await FeatureFlagAPI.getFeatureFlags();
    localStorage.setItem(
      LOCAL_STORAGE_FLAGS_KEY,
      JSON.stringify({
        flags,
        timestamp: Date.now(),
      } as CachedFlags),
    );
    return overrideFlagsFromEnv(flags);
  } catch (error) {
    console.error('Error fetching feature flags:', error);
    return {};
  }
};

export default featureFlags;
